/**
 * @file
 * Drupal.berTags foundation: dom and util helpers, codecs, stores.
 */

((Drupal) => {
  const SORT_STORE_KEY = 'berTagsSort';
  const SORT_FIELDS = ['name', 'created'];

  // Natural, case-insensitive comparison so "tag2" sorts before "tag10".
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  // DOM helpers

  const el = (tag, className) => {
    const node = document.createElement(tag);
    if (className) {
      node.className = className;
    }
    return node;
  };

  // Tooltip and accessible name in one go.
  const labelize = (node, text) => {
    node.title = text;
    node.setAttribute('aria-label', text);
  };

  const button = (className, label) => {
    const node = el('button', className);
    node.type = 'button';
    if (label) {
      labelize(node, label);
    }
    return node;
  };

  // The svg files in icons/ are applied as CSS masks, see .ber-icon.
  const icon = (name) => {
    const node = el('span', `ber-icon ber-icon--${name}`);
    node.setAttribute('aria-hidden', 'true');
    return node;
  };

  const iconButton = (className, label, iconName) => {
    const node = button(className, label);
    node.appendChild(icon(iconName));
    return node;
  };

  const colorDot = (color) => {
    const dot = el('span', 'ber-tag__dot');
    dot.style.background = color;
    dot.setAttribute('aria-hidden', 'true');
    return dot;
  };

  const textSpan = (className, text) => {
    const span = el('span', className);
    span.textContent = text;
    return span;
  };

  // Every show/hide in the widget goes through the is-hidden class.
  const setHidden = (node, hidden) =>
    node.classList.toggle('is-hidden', hidden);
  const isHidden = (node) => node.classList.contains('is-hidden');

  // Click handler that suppresses the default action.
  const onClick = (node, handler) => {
    node.addEventListener('click', (event) => {
      event.preventDefault();
      handler(event);
    });
  };

  const announce = (message, priority) => {
    if (Drupal.announce) {
      Drupal.announce(message, priority);
    }
  };

  // Small utilities

  const debounce = (fn, wait) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const withQuery = (url, query) =>
    `${url}${url.includes('?') ? '&' : '?'}${query}`;

  // Resolves to {ok, data} so callers branch without try/catch nesting.
  const fetchJson = (url, options) =>
    fetch(url, options).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => ({ ok: response.ok, data })),
    );

  const smoothScroll = (node, top) => {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (node.scrollTo && !reduceMotion) {
      node.scrollTo({ top, behavior: 'smooth' });
    } else {
      node.scrollTop = top;
    }
  };

  const scrollToEnd = (node) => smoothScroll(node, node.scrollHeight);

  // Proxy value codecs, matching core's Tags::explode()/implode()

  const explodeTags = (value) => {
    const tags = [];
    let current = '';
    let quoted = false;
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (char === '"') {
        if (quoted && value[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          quoted = !quoted;
        }
      } else if (char === ',' && !quoted) {
        tags.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current.trim() !== '') {
      tags.push(current.trim());
    }
    return tags.filter((tag) => tag !== '');
  };

  const encodeTag = (text) =>
    /[,"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;

  // "Label (id)" per core's autocomplete tags format.
  const parseEntityTag = (tag) => {
    const match = tag.match(/\((\d+)\)$/);
    return match
      ? { id: match[1], label: tag.slice(0, match.index).trim() }
      : null;
  };

  const codecs = { explodeTags, encodeTag, parseEntityTag };

  // Sort preference store

  const sortStore = {
    read() {
      try {
        const parsed = JSON.parse(
          window.localStorage.getItem(SORT_STORE_KEY) || '',
        );
        if (parsed && SORT_FIELDS.includes(parsed.field)) {
          return {
            field: parsed.field,
            dir: parsed.dir === 'desc' ? 'desc' : 'asc',
          };
        }
      } catch (e) {
        // No remembered choice.
      }
      return null;
    },
    write(sort) {
      try {
        window.localStorage.setItem(SORT_STORE_KEY, JSON.stringify(sort || {}));
      } catch (e) {
        // Storage unavailable, the choice still applies for this view.
      }
    },
  };

  // A localStorage-backed key/value map; unavailable storage degrades to
  // per-view choices without errors.
  const store = (storageKey) => ({
    read() {
      try {
        const parsed = JSON.parse(
          window.localStorage.getItem(storageKey) || '',
        );
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (e) {
        return {};
      }
    },
    write(key, value) {
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ ...this.read(), [key]: value }),
        );
      } catch (e) {
        // Storage unavailable, the choice still applies for this view.
      }
    },
  });

  // The view toggles (parents first, glossary) persist like the sort.
  const viewStore = store('berTagsViewToggles');

  // Reads a widget's data attributes into a config object. bool means
  // exactly attribute === '1', the module-wide convention.
  const readConfig = (root, spec) => {
    const config = {};
    Object.entries(spec).forEach(([key, def]) => {
      const raw = root.getAttribute(def.attr);
      if (def.type === 'bool') {
        config[key] = raw === '1';
      } else if (def.type === 'int') {
        config[key] = parseInt(raw, 10) || (def.default ?? 0);
      } else if (def.type === 'json') {
        try {
          config[key] = raw ? JSON.parse(raw) : def.default;
        } catch (e) {
          config[key] = def.default;
        }
      } else {
        config[key] = raw || (def.default ?? '');
      }
    });
    return config;
  };

  // A hidden weight-ordered JSON value: read sorts rows by weight, write
  // stamps weight from the order and notifies the form.
  const orderedValue = (input, idKey) => ({
    read() {
      let rows;
      try {
        rows = JSON.parse(input.value || '[]');
      } catch (e) {
        rows = [];
      }
      return rows
        .filter((row) => row && row[idKey])
        .sort((a, b) => (a.weight || 0) - (b.weight || 0));
    },
    write(rows) {
      input.value = JSON.stringify(
        rows.map((row, index) => ({ ...row, weight: index })),
      );
      input.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    },
  });

  Drupal.berTags = {
    components: {},
    codecs,
    sortStore,
    viewStore,
    dom: {
      el,
      button,
      icon,
      iconButton,
      colorDot,
      textSpan,
      labelize,
      setHidden,
      isHidden,
      onClick,
    },
    util: {
      debounce,
      fetchJson,
      withQuery,
      smoothScroll,
      scrollToEnd,
      announce,
      store,
      readConfig,
      orderedValue,
      collator,
    },
  };
})(Drupal);
